import React, { useCallback, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import "./EbDialog.scss";

// ═════════════════════════════════════════════════════════════════════════════
// EbDialog — the one dialog shell for Execution Blueprint, both apps.
//
// ⚠️ THIS FILE IS INTENTIONALLY IDENTICAL IN TWO REPOSITORIES.
//
//   10x-coach-frontend/src/components/CoachPanel/ExecutionBlueprint/
//     ExecutionBlueprintLiveReview/_shared/EbDialog.js
//   10x-frontend/src/components/UserPanel/ExecutionBlueprint/
//     ExecutionBlueprintLiveReview/_shared/EbDialog.js
//
// `diff` is the test; change one copy, change both in the same pass. It imports
// nothing but React, ReactDOM and its own stylesheet, which is what makes that
// possible — see the other `_shared/` headers for the full rule.
//
// ── WHY IT EXISTS ───────────────────────────────────────────────────────────
// An audit on 2026-08-31 found SEVEN distinct dialog designs across the two
// apps, four of them the same "title, body, cancel/confirm" pattern wearing
// different clothes:
//
//   width      380 / 460 / 480 / 604 / 640 / 680 / 757px
//   radius     16px everywhere except one at 24px
//   padding    20 / 24 / 28 / 32 / 36 / 40px
//   buttons    space-between with arrows, 50/50 with cancel left, right-aligned
//   backdrop   Bootstrap's flat 50% black, or rgba(16,24,32,.55) + 2px blur
//   motion     Bootstrap's slide-down, or a custom fade+rise
//   dismissal  full Bootstrap trap, manual Escape with NO focus trap, or
//              hardened static-while-busy
//   danger red #c13b3b with #faebeb, or #ef4444 with #fef2f2
//
// Nobody chose that; it accumulated. Every new dialog copied whichever one was
// nearest and adjusted. This file is the single answer, and the values below are
// the arbitration — not an average, a decision per property, recorded where the
// decision is.
//
// ── WHY NOT react-bootstrap ─────────────────────────────────────────────────
// Most of the dialogs it replaces wrapped `<Modal>`, and every one of them then
// fought it: seven separate stylesheets carry `border-radius: … !important` to
// beat `.modal-content{border-radius:.3rem}` at equal specificity, and the
// backdrop had to be re-skinned anyway. Bootstrap was supplying a portal, a
// focus trap, Escape and scroll lock — about forty lines, reimplemented below —
// in exchange for a cascade fight in every consumer. The two hand-rolled
// dialogs in the CEO app had already reached the same conclusion independently.
//
// ── THE PART THAT IS NOT COSMETIC ───────────────────────────────────────────
// `busy` is a real contract, not a spinner flag. While it is true the dialog
// cannot be dismissed by Escape, by the backdrop, or by the close button —
// because every one of these dialogs fronts a one-way action (a lock with no
// undo, a permanent delete, a hand-off to a coach), and letting someone dismiss
// the dialog mid-request leaves them staring at a screen that hasn't caught up
// with what they just did. Exactly one dialog got that right before this;
// the rest could be dismissed out from under their own request.
// ═════════════════════════════════════════════════════════════════════════════

// ⚠️ Escaped double quotes rather than a single-quoted string: the CEO repo's
// `quotes` rule runs without `avoidEscape`, so single quotes are an error there
// and this file has to satisfy both repos unchanged.
const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * @param isOpen    Render or not. The dialog owns no internal open state.
 * @param onClose   Escape, backdrop, close button. Ignored entirely while busy.
 * @param busy      An action is in flight — see the header. Locks dismissal.
 * @param size      "sm" 420 · "md" 520 (default) · "lg" 640. Three, not seven.
 * @param tone      "primary" | "danger" | "neutral" — tints the icon chip only.
 * @param icon      Optional node for the chip. Omit it and the chip is gone.
 * @param title     Required. The question or the statement, as a sentence.
 * @param subtitle  Optional supporting line.
 * @param actions   Array of `{ label, onClick, variant, loading, disabled,
 *                  autoFocus }`; `variant` is "primary" | "danger" | "ghost".
 *                  Data rather than children so every call site gets the same
 *                  button sizes and the same order without deciding again.
 * @param align     "end" (default) or "between", for a wizard's back button.
 * @param children  The body. Anything: paragraphs, a list, a form.
 * @param labelledBy/className  Escape hatches for the two odd cases.
 */
const EbDialog = ({
  isOpen,
  onClose,
  busy = false,
  size = "md",
  tone = "primary",
  icon = null,
  title,
  subtitle = null,
  actions = [],
  align = "end",
  children = null,
  className = "",
}) => {
  const boxRef = useRef(null);
  // Whatever had focus before the dialog opened, so it can be given back —
  // otherwise focus falls to <body> on close and a keyboard user is dropped at
  // the top of the page.
  const returnFocusRef = useRef(null);

  const requestClose = useCallback(() => {
    if (busy || !onClose) return;
    onClose();
  }, [busy, onClose]);

  useEffect(() => {
    if (!isOpen) return undefined;
    returnFocusRef.current =
      typeof document !== "undefined" ? document.activeElement : null;

    const node = boxRef.current;
    if (node) {
      // Prefer the action that asked for focus; otherwise the first focusable
      // thing. Never the close button by accident — landing on "dismiss" is a
      // hostile default for a dialog asking a question.
      const preferred = node.querySelector("[data-autofocus]");
      const first = preferred || node.querySelector(FOCUSABLE);
      if (first) first.focus();
    }

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        requestClose();
        return;
      }
      if (event.key !== "Tab" || !boxRef.current) return;
      // The trap. Without it Tab walks straight out into the page behind the
      // backdrop, which is invisible and unreachable by mouse.
      const items = Array.from(boxRef.current.querySelectorAll(FOCUSABLE));
      if (items.length === 0) return;
      const firstItem = items[0];
      const lastItem = items[items.length - 1];
      if (event.shiftKey && document.activeElement === firstItem) {
        event.preventDefault();
        lastItem.focus();
      } else if (!event.shiftKey && document.activeElement === lastItem) {
        event.preventDefault();
        firstItem.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown, true);
    // Scroll lock. Restored to whatever it was rather than to "", so a dialog
    // opened from inside another locked context doesn't unlock the page.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      document.body.style.overflow = previousOverflow;
      const target = returnFocusRef.current;
      if (target && typeof target.focus === "function") target.focus();
    };
  }, [isOpen, requestClose]);

  if (!isOpen) return null;

  const dialog = (
    <div
      className={`ebd-backdrop${busy ? " is-busy" : ""}`}
      // `presentation`, not a button: this is the ABSENCE of the dialog, not a
      // control, and announcing a full-screen button to a screen reader would be
      // a lie. Escape is the keyboard path and it is wired above, which is what
      // makes this a statement rather than a shrug at the a11y rule.
      role="presentation"
      onMouseDown={(event) => {
        // `mousedown` on the backdrop ITSELF, not a click anywhere: a click
        // that starts inside the dialog and ends on the backdrop (selecting
        // text, dragging off a button) would otherwise close it.
        if (event.target === event.currentTarget) requestClose();
      }}
    >
      <div
        className={`ebd ebd--${size} ebd--${tone} ${className}`.trim()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="ebd-title"
        aria-describedby={subtitle ? "ebd-subtitle" : undefined}
        ref={boxRef}
      >
        {onClose ? (
          <button
            type="button"
            className="ebd-close"
            onClick={requestClose}
            disabled={busy}
            aria-label="Close"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path
                d="M5 5l14 14M19 5L5 19"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        ) : null}

        <div className="ebd-head">
          {icon ? (
            <span className="ebd-icon" aria-hidden="true">
              {icon}
            </span>
          ) : null}
          <h2 className="ebd-title" id="ebd-title">
            {title}
          </h2>
          {subtitle ? (
            <p className="ebd-subtitle" id="ebd-subtitle">
              {subtitle}
            </p>
          ) : null}
        </div>

        {children ? <div className="ebd-body">{children}</div> : null}

        {actions.length > 0 ? (
          <div className={`ebd-actions ebd-actions--${align}`}>
            {actions.map((action) => (
              <button
                key={action.label}
                type="button"
                className={`ebd-btn ebd-btn--${action.variant || "ghost"}`}
                onClick={action.onClick}
                disabled={action.disabled || (busy && !action.loading)}
                data-autofocus={action.autoFocus ? "" : undefined}
              >
                {action.loading ? (
                  <span className="ebd-spinner" aria-hidden="true" />
                ) : null}
                {action.loading
                  ? action.loadingLabel || action.label
                  : action.label}
              </button>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );

  // Portalled to `body` unconditionally. One consumer NEEDS it — the CEO's chat
  // panel sets `transform`, which makes a `position: fixed` child position
  // against that ancestor instead of the viewport — and doing it for everyone
  // means no call site has to know whether it is the one in a transformed tree.
  return typeof document !== "undefined"
    ? ReactDOM.createPortal(dialog, document.body)
    : dialog;
};

export default EbDialog;
